import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Trash2, ImageUp, FileSignature, Award, Radio, Shield, BadgeCheck, RotateCcw } from "lucide-react";
import { FLAG_STATES } from "@/lib/flags";

const FORM_STORAGE_KEY = "issue_form_v2";

const ENDORSEMENT_TYPES = [
  {
    id: "CoC Endorsement",
    title: "CoC Endorsement",
    desc: "Main Certificate of Endorsement (4 functions)",
    icon: Award,
  },
  {
    id: "GMDSS General Operator",
    title: "GMDSS General Operator",
    desc: "Radio Communication endorsement",
    icon: Radio,
  },
  {
    id: "Ship Security Officer (SSO)",
    title: "Ship Security Officer (SSO)",
    desc: "Ship Security endorsement",
    icon: Shield,
  },
  {
    id: "Certificate of Proficiency",
    title: "Certificate of Proficiency",
    desc: "Specialty (e.g. Advanced Oil Tanker, Chemical, LNG)",
    icon: BadgeCheck,
  },
];

const DEFAULT_FUNCTIONS = [
  { function: "NAVIGATION", level: "MANAGEMENT", limitations: "NONE" },
  { function: "CARGO HANDLING & STOWAGE", level: "MANAGEMENT", limitations: "NONE" },
  { function: "RADIOCOMMUNICATION", level: "OPERATIONAL", limitations: "NONE" },
  {
    function: "CONTROLLING THE OPERATION OF THE SHIP AND CARE FOR PERSONS ON BOARD",
    level: "MANAGEMENT",
    limitations: "NONE",
  },
];

const LEVEL_OPTIONS = ["MANAGEMENT", "OPERATIONAL", "SUPPORT"];

export default function IssuePage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const DEFAULT_FORM = {
    flag_state: "Republic of Zambia",
    endorsement_types: ["CoC Endorsement"],
    coc_number: "",
    holder_name: "",
    holding_certificate_no: "",
    issued_by_country: "REPUBLIC OF HONDURAS",
    rank: "CHIEF MATE",
    regulation: "I/10",
    valid_until: "",
    coc_issue_date: "",
    functions: DEFAULT_FUNCTIONS,
    capacities: [{ capacity: "CHIEF MATE", limitation: "VESSELS OF 3000GT OR MORE" }],
    issued_on: "",
    place_of_issue: "",
    date_of_birth: "",
    nationality: "",
    photo_base64: "",
    signature_base64: "",
  };

  const loadSavedForm = () => {
    try {
      const raw = localStorage.getItem(FORM_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return parsed;
    } catch (e) {}
    return null;
  };

  const saved = loadSavedForm();
  const [form, setForm] = useState(saved ? { ...DEFAULT_FORM, ...saved } : DEFAULT_FORM);
  const [photoPreview, setPhotoPreview] = useState(saved?.photo_base64 || "");
  const [signaturePreview, setSignaturePreview] = useState(saved?.signature_base64 || "");

  // Persist form to localStorage on every change
  useEffect(() => {
    try {
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(form));
    } catch (e) {
      // localStorage quota exceeded (very large photos) — silently ignore
    }
  }, [form]);

  const resetForm = () => {
    localStorage.removeItem(FORM_STORAGE_KEY);
    setForm(DEFAULT_FORM);
    setPhotoPreview("");
    setSignaturePreview("");
    toast.success("Form cleared");
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleType = (id) => {
    setForm((f) => {
      const has = f.endorsement_types.includes(id);
      const next = has
        ? f.endorsement_types.filter((t) => t !== id)
        : [...f.endorsement_types, id];
      return { ...f, endorsement_types: next };
    });
  };

  const updateFn = (i, k, v) => {
    const fns = [...form.functions];
    fns[i] = { ...fns[i], [k]: v };
    update("functions", fns);
  };
  const addFn = () =>
    update("functions", [
      ...form.functions,
      { function: "", level: "OPERATIONAL", limitations: "NONE" },
    ]);
  const removeFn = (i) =>
    update("functions", form.functions.filter((_, idx) => idx !== i));

  const updateCap = (i, k, v) => {
    const caps = [...form.capacities];
    caps[i] = { ...caps[i], [k]: v };
    update("capacities", caps);
  };
  const addCap = () =>
    update("capacities", [...form.capacities, { capacity: "", limitation: "" }]);
  const removeCap = (i) =>
    update("capacities", form.capacities.filter((_, idx) => idx !== i));

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setPhotoPreview(dataUrl);
      update("photo_base64", dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const onSignature = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setSignaturePreview(dataUrl);
      update("signature_base64", dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.endorsement_types.length === 0) {
      toast.error("Select at least one endorsement type");
      return;
    }
    setSubmitting(true);
    const { endorsement_types, ...rest } = form;
    try {
      const created = [];
      for (const type of endorsement_types) {
        const { data } = await api.post("/certificates", {
          ...rest,
          endorsement_type: type,
        });
        created.push(data);
      }
      toast.success(
        created.length === 1
          ? "Endorsement issued"
          : `${created.length} endorsements issued`
      );
      if (created.length === 1) {
        navigate(`/certificate/${created[0].id}`);
      } else {
        navigate("/");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Failed to issue");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen parchment" data-testid="issue-page">
      <header className="bg-[#0a1d3f] text-[#f4ecd5] border-b-4 border-[#d4af37]">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            className="text-[#f4ecd5] hover:bg-[#d4af37]/10 hover:text-[#f4ecd5]"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Ledger
          </Button>
          <div className="font-serif-display tracking-[0.25em] text-sm">
            ISSUE NEW ENDORSEMENT
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <form onSubmit={onSubmit} className="space-y-6" data-testid="issue-form">
          {/* Endorsement Type selector (multi-select) */}
          <Card className="rounded-none border-stone-400 bg-white/70">
            <CardHeader>
              <CardTitle className="font-serif-display text-[#0a1d3f] tracking-wider text-xl">
                Endorsement Types *{" "}
                <span className="text-sm font-serif-body font-normal text-stone-600">
                  (select one or more — one certificate will be issued per type)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {ENDORSEMENT_TYPES.map((t) => {
                const Icon = t.icon;
                const selected = form.endorsement_types.includes(t.id);
                return (
                  <button
                    type="button"
                    key={t.id}
                    onClick={() => toggleType(t.id)}
                    className={`text-left rounded-none border-2 p-4 transition-colors ${
                      selected
                        ? "border-[#d4af37] bg-[#fff9e6]"
                        : "border-stone-300 bg-white hover:border-[#0a1d3f]/40"
                    }`}
                    data-testid={`endorsement-type-${t.id.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-1 h-5 w-5 rounded-sm border-2 flex items-center justify-center flex-shrink-0 ${
                        selected ? "border-[#d4af37] bg-[#0a1d3f]" : "border-stone-400 bg-white"
                      }`}>
                        {selected && <span className="text-[#d4af37] text-xs leading-none">✓</span>}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-[#0a1d3f]" />
                          <span className="font-serif-display font-bold text-[#0a1d3f]">
                            {t.title}
                          </span>
                        </div>
                        <div className="text-sm text-stone-700 mt-1 font-serif-body">
                          {t.desc}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </CardContent>
          </Card>

          <Card className="rounded-none border-stone-400 bg-white/70">
            <CardHeader>
              <CardTitle className="font-serif-display text-[#0a1d3f] tracking-wider text-xl">
                Identification
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Flag State">
                <Select
                  value={form.flag_state}
                  onValueChange={(v) => update("flag_state", v)}
                >
                  <SelectTrigger
                    className="rounded-none bg-white border-stone-400"
                    data-testid="flag-state-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FLAG_STATES.map((f) => (
                      <SelectItem key={f.name} value={f.name}>
                        {f.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="COC Number">
                <Input
                  required
                  value={form.coc_number}
                  onChange={(e) => update("coc_number", e.target.value.toUpperCase())}
                  placeholder="E665213"
                  className="rounded-none bg-white border-stone-400 font-mono"
                  data-testid="coc-number-input"
                />
              </Field>
              <Field label="Holder Full Name">
                <Input
                  required
                  value={form.holder_name}
                  onChange={(e) => update("holder_name", e.target.value.toUpperCase())}
                  placeholder="MAIDUL SHEIKA"
                  className="rounded-none bg-white border-stone-400"
                  data-testid="holder-name-input"
                />
              </Field>
              <Field label="Holding Certificate №">
                <Input
                  required
                  value={form.holding_certificate_no}
                  onChange={(e) =>
                    update("holding_certificate_no", e.target.value.toUpperCase())
                  }
                  placeholder="T0071592"
                  className="rounded-none bg-white border-stone-400 font-mono"
                  data-testid="holding-cert-input"
                />
              </Field>
              <Field label="Issued By (Origin Country)">
                <Input
                  required
                  value={form.issued_by_country}
                  onChange={(e) =>
                    update("issued_by_country", e.target.value.toUpperCase())
                  }
                  className="rounded-none bg-white border-stone-400"
                  data-testid="issued-by-input"
                />
              </Field>
              <Field label="Rank">
                <Input
                  required
                  value={form.rank}
                  onChange={(e) => update("rank", e.target.value.toUpperCase())}
                  className="rounded-none bg-white border-stone-400"
                  data-testid="rank-input"
                />
              </Field>
              <Field label="Regulation">
                <Input
                  required
                  value={form.regulation}
                  onChange={(e) => update("regulation", e.target.value)}
                  className="rounded-none bg-white border-stone-400"
                  data-testid="regulation-input"
                />
              </Field>
              <Field label="Endorsement Valid Until (DD.MM.YYYY) — same as COC Valid Until">
                <Input
                  required
                  value={form.valid_until}
                  onChange={(e) => update("valid_until", e.target.value)}
                  placeholder="25.02.2027"
                  className="rounded-none bg-white border-stone-400"
                  data-testid="valid-until-input"
                />
              </Field>
              <Field label="COC Issue Date (DD/MM/YYYY)">
                <Input
                  value={form.coc_issue_date}
                  onChange={(e) => update("coc_issue_date", e.target.value)}
                  placeholder="10/05/2022"
                  className="rounded-none bg-white border-stone-400"
                  data-testid="coc-issue-date-input"
                />
              </Field>
            </CardContent>
          </Card>

          {/* Functions */}
          <Card className="rounded-none border-stone-400 bg-white/70">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-serif-display text-[#0a1d3f] tracking-wider text-xl">
                Functions
              </CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addFn}
                className="rounded-none border-[#0a1d3f] text-[#0a1d3f]"
                data-testid="add-function-btn"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Function
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {form.functions.map((fn, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start">
                  <Input
                    value={fn.function}
                    onChange={(e) => updateFn(i, "function", e.target.value.toUpperCase())}
                    className="col-span-6 rounded-none bg-white border-stone-400"
                    placeholder="FUNCTION"
                    data-testid={`fn-name-${i}`}
                  />
                  <Select
                    value={fn.level}
                    onValueChange={(v) => updateFn(i, "level", v)}
                  >
                    <SelectTrigger className="col-span-3 rounded-none bg-white border-stone-400">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEVEL_OPTIONS.map((l) => (
                        <SelectItem key={l} value={l}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={fn.limitations}
                    onChange={(e) => updateFn(i, "limitations", e.target.value.toUpperCase())}
                    className="col-span-2 rounded-none bg-white border-stone-400"
                    placeholder="LIMITATIONS"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFn(i)}
                    className="col-span-1 text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Capacities */}
          <Card className="rounded-none border-stone-400 bg-white/70">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-serif-display text-[#0a1d3f] tracking-wider text-xl">
                Serving Capacities
              </CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addCap}
                className="rounded-none border-[#0a1d3f] text-[#0a1d3f]"
                data-testid="add-capacity-btn"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Capacity
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {form.capacities.map((cap, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start">
                  <Input
                    value={cap.capacity}
                    onChange={(e) => updateCap(i, "capacity", e.target.value.toUpperCase())}
                    className="col-span-5 rounded-none bg-white border-stone-400"
                    placeholder="CAPACITY"
                    data-testid={`cap-name-${i}`}
                  />
                  <Input
                    value={cap.limitation}
                    onChange={(e) => updateCap(i, "limitation", e.target.value.toUpperCase())}
                    className="col-span-6 rounded-none bg-white border-stone-400"
                    placeholder="LIMITATION (IF ANY)"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeCap(i)}
                    className="col-span-1 text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Issuance details */}
          <Card className="rounded-none border-stone-400 bg-white/70">
            <CardHeader>
              <CardTitle className="font-serif-display text-[#0a1d3f] tracking-wider text-xl">
                Issuance Details
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Certificate of Equivalent №">
                <Input
                  value={form.cert_equivalent_no || ""}
                  onChange={(e) =>
                    update("cert_equivalent_no", e.target.value.toUpperCase())
                  }
                  className="rounded-none bg-stone-100 border-stone-400 font-mono"
                  placeholder="Auto-generated (leave blank)"
                  data-testid="equiv-no-input"
                />
              </Field>
              <Field label="Issued On (DD/MM/YYYY)">
                <Input
                  required
                  value={form.issued_on}
                  onChange={(e) => update("issued_on", e.target.value)}
                  placeholder="23/04/2026"
                  className="rounded-none bg-white border-stone-400"
                  data-testid="issued-on-input"
                />
              </Field>
              <Field label="Place of Issue">
                <Input
                  required
                  value={form.place_of_issue}
                  onChange={(e) =>
                    update("place_of_issue", e.target.value.toUpperCase())
                  }
                  placeholder="CALIFORNIA, USA"
                  className="rounded-none bg-white border-stone-400"
                  data-testid="place-input"
                />
              </Field>
              <Field label="Date of Birth (DD/MM/YYYY)">
                <Input
                  required
                  value={form.date_of_birth}
                  onChange={(e) => update("date_of_birth", e.target.value)}
                  placeholder="15/01/1984"
                  className="rounded-none bg-white border-stone-400"
                  data-testid="dob-input"
                />
              </Field>
              <Field label="Nationality">
                <Input
                  required
                  value={form.nationality}
                  onChange={(e) =>
                    update("nationality", e.target.value.toUpperCase())
                  }
                  placeholder="INDIAN"
                  className="rounded-none bg-white border-stone-400"
                  data-testid="nationality-input"
                />
              </Field>
              <Field label="Holder Photograph (top-right of certificate)">
                <label
                  htmlFor="photo"
                  className="flex items-center gap-3 border border-stone-400 bg-white px-3 h-10 cursor-pointer hover:bg-stone-50"
                  data-testid="photo-input-label"
                >
                  <ImageUp className="h-4 w-4 text-[#0a1d3f]" />
                  <span className="font-serif-body text-sm text-stone-700">
                    {photoPreview ? "Replace photo" : "Upload passport-size photo"}
                  </span>
                  <input
                    id="photo"
                    type="file"
                    accept="image/*"
                    onChange={onPhoto}
                    className="hidden"
                    data-testid="photo-input"
                  />
                  {photoPreview && (
                    <img
                      src={photoPreview}
                      alt="preview"
                      className="ml-auto h-8 w-8 object-cover border border-stone-400"
                    />
                  )}
                </label>
              </Field>
              <Field label="Holder Signature (image)">
                <label
                  htmlFor="signature"
                  className="flex items-center gap-3 border border-stone-400 bg-white px-3 h-10 cursor-pointer hover:bg-stone-50"
                  data-testid="signature-input-label"
                >
                  <FileSignature className="h-4 w-4 text-[#0a1d3f]" />
                  <span className="font-serif-body text-sm text-stone-700">
                    {signaturePreview ? "Replace signature" : "Upload signature image (PNG with transparent bg recommended)"}
                  </span>
                  <input
                    id="signature"
                    type="file"
                    accept="image/*"
                    onChange={onSignature}
                    className="hidden"
                    data-testid="signature-input"
                  />
                  {signaturePreview && (
                    <img
                      src={signaturePreview}
                      alt="signature preview"
                      className="ml-auto h-8 w-16 object-contain border border-stone-400 bg-white"
                    />
                  )}
                </label>
              </Field>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={resetForm}
              className="rounded-none border-stone-500 text-stone-800"
              data-testid="reset-form-btn"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Clear saved
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/")}
              className="rounded-none border-stone-500 text-stone-800"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#0a1d3f] hover:bg-[#13295a] text-[#f4ecd5] font-serif-display tracking-widest rounded-none h-11 px-6 border border-[#d4af37]/40"
              data-testid="submit-cert-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {submitting ? "Issuing…" : `Issue ${form.endorsement_types.length || 0} Endorsement${form.endorsement_types.length === 1 ? "" : "s"}`}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="font-serif-body text-stone-800 text-xs uppercase tracking-widest">
        {label}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
